<script setup lang="ts">
import { computed } from 'vue'
import { useModalForm } from '@/shared/lib'
import { DatePicker } from '@/shared/ui/DatePicker'
import { EmptyState } from '@/shared/ui/EmptyState'
import { FormItem } from '@/shared/ui/Form'
import { FormModal } from '@/shared/ui/FormModal'
import { Input } from '@/shared/ui/Input'
import { Skeleton } from '@/shared/ui/Skeleton'
import { Stack } from '@/shared/ui/Stack'

import {
  UserNoteCard,
  useCreateUserNote,
  useUserNotes
} from '@/apps/adok/entities/user-note'

import { useUserNoteCreateForm } from '../model/form'

const props = defineProps<{ modelValue?: boolean }>()

const emit = defineEmits<{ (e: 'close'): void }>()

const isOpen = computed(() => !!props.modelValue)

const SKELETON_NOTES = 2

// The sidebar mounts this on every page a staff member opens, so the history waits for
// somebody to ask for it rather than costing a request per page. `useCreate` invalidates
// the list, so a reopened panel shows the note that was just written.
const { items: notes, isLoading } = useUserNotes({}, { enabled: isOpen })

const noteForm = useUserNoteCreateForm()
const { form, errors, formErrors } = noteForm

const { submit, isSubmitting, close } = useModalForm({
  entityForm: noteForm,
  mutation: useCreateUserNote(),
  onClose: () => emit('close'),
  successMessage: 'The note has been added.'
})
</script>

<template>
  <FormModal
    :model-value="modelValue"
    title="User notes"
    width="520px"
    :errors="formErrors"
    :submitting="isSubmitting"
    save-label="Add note"
    @close="close"
    @submit="submit"
  >
    <FormItem label="Note" :error="errors.text" required>
      <Input
        v-model="form.text"
        type="textarea"
        :autosize="{ minRows: 5, maxRows: 20 }"
        placeholder="Type the note..."
      />
    </FormItem>

    <FormItem label="Notify at" :error="errors.notifyAt">
      <DatePicker
        v-model="form.notifyAt"
        value-format="YYYY-MM-DD"
        placeholder="Choose a date"
        block
      />
    </FormItem>

    <template #after>
      <Stack v-if="isLoading" :gap="12">
        <Stack v-for="row in SKELETON_NOTES" :key="row" :gap="8">
          <Skeleton width="40%" />
          <Skeleton />
        </Stack>
      </Stack>
      <EmptyState v-else-if="!notes.length" description="No notes yet" />
      <Stack v-else :gap="8">
        <UserNoteCard v-for="note in notes" :key="note.id" :note="note" />
      </Stack>
    </template>
  </FormModal>
</template>
